import React, { useContext, useState, useEffect } from 'react';
import * as Highcharts from 'highcharts';
import { scaleLinear } from '@vx/scale';
import HighchartsReact from 'highcharts-react-official';
import HC_exporting from 'highcharts/modules/exporting';
import HC_offlineExporting from 'highcharts/modules/offline-exporting';
import HC_patternFill from 'highcharts/modules/pattern-fill';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { colors } from '@alixpartners/ui-components';

import { getFormattedCurrencyWithIdealDenomination } from 'utils/number';
import { applyMaxPointWidthWrapper } from './utils';
import DetailsChartHighlightBlock from 'Details/DetailsChart/DetailsChartHighlightBlock';

import {
	Wrapper,
	ChartWrapper,
	UnitsLabelBlock,
	NoDataAvailableContainer,
	NoDataAvailableLabel,
	SummaryBackgroundWrapper,
	SummaryTriangle,
} from './styles';

HC_exporting(Highcharts);
HC_offlineExporting(Highcharts);
HC_patternFill(Highcharts);
applyMaxPointWidthWrapper(Highcharts);

export const CHART_TYPES = {
	waterfall: 'waterfall',
	line: 'line',
	bar: 'bar',
	scatter: 'scatter',
	stackedBar: 'stackedBar',
	stackedBar100: 'stackedBar100',
};

const CHART_TYPE_MAP = {
	[CHART_TYPES.line]: 'line',
	[CHART_TYPES.bar]: 'column',
	[CHART_TYPES.scatter]: 'scatter',
	[CHART_TYPES.stackedBar]: 'column',
	[CHART_TYPES.stackedBar100]: 'column',
	[CHART_TYPES.waterfall]: 'waterfall',
};

const TOOLTIP_COUNT_IN_COL = 5;
const STACKED_LABEL_HEIGHT = 14;

export const HeroChart = props => {
	const {
		categories,
		series,
		currency,
		currencySymbol,
		type,
		setChartRef,
		pointsColorsForSeries,
		isPptExport,
		showNetSpend,
		hideDecimals: hideDecimalsProp,
		hideIndex,
		maxValue,
		minValue,
		setChartMinMax,
		categoryLevel,
	} = props;

	let processedSeries = series;
	let unitsLabel = currency.toUpperCase();
	let timeoutRef = null;

	const { permissions } = useContext(PermissionsContext);

	let formatPrice = (value, hideCurrency) => {
		let formattedPrice = getFormattedCurrencyWithIdealDenomination({
			value,
			currencyFormat: {
				currency: currency && currency.toUpperCase(),
			},
			isUnitSign: true,
			isCurrencySign: false,
		});

		if (!hideCurrency) {
			formattedPrice = `${currencySymbol}${formattedPrice}`;
		}

		return formattedPrice;
	};

	if (type === CHART_TYPES.stackedBar100) {
		unitsLabel = '%';
		formatPrice = value => (hideDecimalsProp ? Math.round(value) : value) + '%';
	} else if (type === CHART_TYPES.line) {
		const selectedConfig = {
			lineWidth: 5,
			className: 'selected-series',
		};
		processedSeries = series.map(
			({ selected, ...rest }) =>
				selected ? { ...rest, ...selectedConfig } : rest
		);
	}

	processedSeries = processedSeries.map(({ data, ...rest }, i) => {
		const res = {
			data: data.map((value, j) => {
				let result = {
					name: categories[j],
					y: value,
					color: pointsColorsForSeries[i][j],
					visible: j !== hideIndex,
					lineWidth: 0,
				};

				if (value.value || value.value === 0) {
					const { value: y, ...rest } = value;

					result = {
						...result,
						y,
						...rest,
					};
				}

				return result;
			}),
			...rest,
			color: pointsColorsForSeries[i][0],
		};

		return res;
	});

	const colCount = series.length > TOOLTIP_COUNT_IN_COL ? 2 : 1;
	const showLegend = processedSeries.length > 1;

	const svgElementClassName = isPptExport
		? 'details-chart-ppt-export'
		: 'details-chart';

	const chartOptions = {
		exporting: {
			buttons: {
				contextButton: {
					enabled: false,
				},
			},
		},
		chart: {
			style: {
				fontFamily: isPptExport ? 'Verdana' : 'Roboto',
			},
			height: 380,
			type: type ? CHART_TYPE_MAP[type] || type : CHART_TYPE_MAP.bar,
			backgroundColor: 'rgba(0,0,0,0)',
			events: {
				load: function({ target }) {
					const chart = this;

					target.container.style.overflow = 'visible';
					target.container.firstChild.classList.add(svgElementClassName);

					if (type === CHART_TYPES.waterfall || type === CHART_TYPES.bar) {
						applyWaterfallLabelsFixes({ chart, type, categoryLevel, series });
					}
				},
				redraw: function() {
					if (!chartRef.current || !chartRef.current.chart) {
						return;
					}

					const chart = chartRef.current.chart;
					if (highlightBlockHeight !== chart.plotHeight) {
						setHighlightBlockHeight(chart.plotHeight);
					}
					if (highlightBlockWidth !== chart.plotWidth) {
						setHighlightBlockWidth(chart.plotWidth);
					}
					if (type === CHART_TYPES.waterfall || type === CHART_TYPES.bar) {
						applyWaterfallLabelsFixes({ chart, type, categoryLevel, series });
					}
				},
			},
		},
		title: {
			text: null,
		},
		xAxis: {
			reversed: type === CHART_TYPES.waterfall,
			categories,
			crosshair:
				type === CHART_TYPES.line || type === CHART_TYPES.scatter
					? {
							color: colors.grey7,
							dashStyle: 'dot',
							width: 1,
					  }
					: null,
			labels: {
				style: {
					fontSize: '11px',
					fontWeight: '400',
					lineHeight: '13px',
					letterSpacing: 0,
					color: colors.grey8,
				},
			},
			lineColor: colors.black,
		},
		legend: {
			enabled: showLegend,
			symbolRadius: 0,
			symbolHeight: 16,
			symbolPadding: 8,
			itemDistance: 32,
			itemMarginBottom: 8,
			align: 'center',
			itemStyle: {
				color: colors.grey8,
				cursor: 'pointer',
				fontSize: '11px',
				fontWeight: 'normal',
				textOverflow: 'ellipsis',
			},
		},
		yAxis: {
			visible: type !== CHART_TYPES.waterfall,
			labels: {
				formatter: function() {
					return `<strong>${formatPrice(this.value, false, true)}</strong>`;
				},
			},
			useHTML: true,
			lineWidth: 1,
			lineColor: colors.black,
			gridLineWidth: 0,
			minorGridLineWidth: 0,
			stackLabels: {
				enabled: false,
			},
			title: {
				enabled: false,
			},
			plotLines: [
				{
					value: 0,
					width: 1,
					color: colors.black,
					zIndex: 10,
				},
			],

			...(type === CHART_TYPES.stackedBar100
				? {
						max: 100,
						min: 0,
				  }
				: {}),
		},
		credits: {
			enabled: false,
		},
		tooltip: {
			enabled: false,
		},
		series: processedSeries,
		plotOptions: {
			line: {
				states: {
					hover: {
						symbol: 'circle',
					},
				},
			},

			waterfall: {
				stacking: 'normal',
				borderWidth: 0,
				dataLabels: {
					enabled: true,
					className: 'bar-label',
					style: {
						fontSize: '11px',
						fontWeight: '500',
					},
					color: 'black',
					formatter: function() {
						const formattedPrice = formatPrice(this.y, false, false);

						let color = this.point.color;
						if (color.pattern) {
							color = color.pattern.path.stroke;
						}

						return `<span style="color:${color}; text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff;">${formattedPrice}</span>`;
					},
					overflow: 'none',
					crop: false,
					inside: false,
					position: 'top',
					allowOverlap: true,
					useHTML: true,
				},
			},

			series: {
				animation: false,
				stacking: 'normal',
				pointPadding: 0,
				dataLabels: {
					enabled: true,
					className: 'bar-label',
					style: {
						fontSize: '11px',
						fontWeight: '500',
					},
					color: 'black',
					formatter: function() {
						const formattedPrice = formatPrice(this.y, false, false);

						let color = this.point.color;
						if (color.pattern) {
							color = color.pattern.path.stroke;
						}

						return `<span style="color:${color}">${formattedPrice}</span>`;
					},
					overflow: 'none',
					crop: false,
					inside: false,
					position: 'top',
					allowOverlap: true,
					useHTML: true,
				},
				marker: {
					enabled: type === CHART_TYPES.scatter,
					symbol: 'circle',
				},
				states: {
					hover: {
						enabled: type === CHART_TYPES.scatter || type === CHART_TYPES.line,
						marker: {
							enabled: true,
							symbol: 'circle',
							fillColor: colors.white,
						},
					},
				},
				events: {
					afterAnimate: function() {
						if (!chartRef.current || !chartRef.current.chart) {
							return;
						}

						clearTimeout(timeoutRef);
						timeoutRef = setTimeout(() => {
							if (!chart || !chart.container || !chart.container.classList) {
								return;
							}
							applyWaterfallLabelsFixes({ chart, type, categoryLevel, series });
							chart.container.classList.remove('highcharts-container-loading');
						}, 1000);
					},
				},
			},
			column:
				type === CHART_TYPES.stackedBar || type === CHART_TYPES.stackedBar100
					? {
							stacking:
								type === CHART_TYPES.stackedBar100 ? 'percent' : 'normal',
							dataLabels: {
								enabled: false,
							},
					  }
					: {},
			scatter: {
				marker: {
					radius: 5,
					states: {
						hover: {
							enabled: true,
							lineColor: 'rgb(100,100,100)',
						},
					},
				},
				states: {
					hover: {
						marker: {
							enabled: false,
						},
					},
				},
			},
		},
	};

	const [highlightBlockHeight, setHighlightBlockHeight] = useState(0);
	const [highlightBlockWidth, setHighlightBlockWidth] = useState(0);
	const wrapperRef = React.useRef(null);
	const chartRef = React.useRef(null);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useLayoutEffect(() => {
		if (chartRef.current && chartRef.current.chart) {
			setChartRef && setChartRef(chartRef);

			setHighlightBlockHeight(chartRef.current.chart.plotHeight);
		}
	});

	const showNoDataAvailable = !categories || categories.length === 0;
	const chart = chartRef.current && chartRef.current.chart;
	const refreshKey = `key_${type}_${showNetSpend}`;

	let selectedLeafIndex = props.highlightItemIndex;

	useEffect(
		() => {
			const chart = chartRef.current && chartRef.current.chart;

			if (chart) {
				const chartExtremes = chart.yAxis[0].getExtremes();
				let minValue = chartExtremes.dataMin;
				let maxValue = chartExtremes.dataMax;

				minValue = Math.round(minValue);
				minValue = Math.min(0, minValue);

				const scale = scaleLinear({
					domain: [0, chart.plotHeight],
					rangeRound: [0, maxValue - minValue],
				});

				const maxOverride =
					maxValue +
					scale(STACKED_LABEL_HEIGHT * series.length + STACKED_LABEL_HEIGHT);

				chartOptions.yAxis.max = maxOverride;
				chartOptions.yAxis.min = minValue;

				setChartMinMax({ minValue, maxValue: maxOverride });
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[chartRef, series]
	);

	if (chart) {
		if (chart.container && chart.container.classList) {
			chart.container.classList.add('highcharts-container-loading');

			if (maxValue) {
				chartOptions.yAxis.max = maxValue;
				chartOptions.yAxis.min = minValue;
			}
		}
	}

	return (
		<React.Fragment>
			{showNoDataAvailable && (
				<NoDataAvailableContainer>
					<NoDataAvailableLabel>No data available</NoDataAvailableLabel>
				</NoDataAvailableContainer>
			)}

			<Wrapper
				columns={colCount}
				innerRef={wrapperRef}
				seriesColors={pointsColorsForSeries}
				svgElementClassName={svgElementClassName}
			>
				{chart && (
					<DetailsChartHighlightBlock
						{...props}
						permissions={permissions}
						left={chart.plotLeft}
						width={highlightBlockWidth}
						height={highlightBlockHeight + 36}
						selectedLeafIndex={selectedLeafIndex}
						hideIndex={categories.length - 1 - hideIndex}
					/>
				)}
				{type === CHART_TYPES.waterfall && (
					<HeroChartSummaryBackground
						height={highlightBlockHeight + 36}
						width={
							(categoryLevel === 0
								? 2 * highlightBlockWidth
								: 1 * highlightBlockWidth) /
								categories.length +
							20
						}
					/>
				)}
				<UnitsLabelBlock>{unitsLabel}</UnitsLabelBlock>
				<ChartWrapper key={refreshKey}>
					<HighchartsReact
						highcharts={Highcharts}
						options={chartOptions}
						ref={chartRef}
					/>
				</ChartWrapper>
			</Wrapper>
		</React.Fragment>
	);
};

const HeroChartSummaryBackground = ({ width, height }) => {
	const triangleSize = 10;
	return (
		<SummaryBackgroundWrapper width={width || 0} height={height || 0}>
			<SummaryTriangle size={triangleSize} top={height / 2 - triangleSize} />
		</SummaryBackgroundWrapper>
	);
};

const applyWaterfallLabelsFixes = ({
	chart,
	type,
	categoryLevel,
	series: dataSeries,
}) => {
	if (!chart || !chart.series) {
		return;
	}

	const yLabelsPositions = [];
	const seriesLength = chart.series.length;
	// position of the labels

	chart.series.forEach((series, i) => {
		// onAnimate triggers multiple times, sometimes with series.points as null
		if (!series.points) {
			return;
		}
		series.points.forEach((point, j) => {
			const dataLabel = point.dataLabel;

			if (!dataLabel) return;

			const dataValue = getDataValueByIndex(dataSeries, i, j);
			const isLastSeries = i === chart.series.length - 1;

			if (!yLabelsPositions[j] && (dataValue > 0 || isLastSeries)) {
				yLabelsPositions[j] = dataLabel.y;
			}
		});
	});

	chart.series.forEach((series, i) => {
		// onAnimate triggers multiple times, sometimes with series.points as null
		if (!series.points) {
			return;
		}
		series.points.forEach((point, j) => {
			const dataLabel = point.dataLabel;
			if (!dataLabel) return;

			const y =
				yLabelsPositions[j] - STACKED_LABEL_HEIGHT * (seriesLength - 1 - i);

			dataLabel.translate(dataLabel.x, y);
		});

		// remove dashed line from waterfall chart
		if (type === CHART_TYPES.waterfall) {
			const pathArray = series.getCrispPath();
			if (categoryLevel === 0) {
				series.graph.attr({
					d: [...pathArray.slice(0, -6), ...pathArray.slice(-2)],
				});
			} else {
				series.graph.attr({
					d: pathArray.slice(0, -4),
				});
			}
		}
	});
};

const getDataValueByIndex = (dataSeries, i, j) => {
	const series = dataSeries[i];
	let dataValue = 0;

	if (series && series.data) {
		dataValue = series.data[j];

		// check if dataValue includes pattern together with value
		if (dataValue && (dataValue.value || dataValue.value === 0)) {
			dataValue = dataValue.value;
		}
	}

	return dataValue;
};

export default React.memo(HeroChart);
